import { useMemo, useState } from "react";
import { Check, ChevronsUpDown } from "lucide-react";
import { cn } from "../../lib/utils";
import { Button } from "../ui/button";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "../ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { matchesQuery, type TreeOption } from "./treeOptions";

const NONE_VALUE = "__none__";

// "Billing › Refunds › Partial refunds" in a narrow field: the parents shorten first, so the chosen one stays readable
function PathLabel({ option }: { option: TreeOption }) {
  // The separator's trailing space would be dropped at the end of the span, so it becomes a non-breaking one
  const parents = option.path.endsWith(option.name)
    ? option.path.slice(0, option.path.length - option.name.length).replace(/ $/, " ")
    : "";
  return (
    <>
      {parents && <span className="min-w-0 truncate text-muted-foreground">{parents}</span>}
      <span className={cn("truncate", parents && "max-w-full shrink-0")}>{option.name}</span>
    </>
  );
}

interface TreeSelectProps {
  options: TreeOption[];
  value: string | null | undefined;
  onChange: (id: string | null) => void;
  placeholder?: string;
  // When set, a first row clears the value ("No category")
  noneLabel?: string;
  searchPlaceholder?: string;
  emptyMessage?: string;
  disabled?: boolean;
  id?: string;
  className?: string;
  "aria-invalid"?: boolean;
}

/**
 * Searchable single-select for a tree (ticket categories, knowledge base collections). Rows are indented by depth;
 * typing matches the whole path, so "refund" finds "Billing › Refunds", and matches show their full path.
 */
export function TreeSelect({
  options,
  value,
  onChange,
  placeholder = "Select…",
  noneLabel,
  searchPlaceholder = "Search…",
  emptyMessage = "Nothing found",
  disabled,
  id,
  className,
  "aria-invalid": ariaInvalid,
}: TreeSelectProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");

  const selected = options.find((o) => o.id === value);
  const searching = query.trim().length > 0;
  const visible = useMemo(() => options.filter((o) => matchesQuery(o, query)), [options, query]);

  const select = (next: string | null) => {
    onChange(next);
    setOpen(false);
    setQuery("");
  };

  return (
    // Modal, like a Select: inside a dialog, a non-modal popover can't be wheel-scrolled, because the dialog's scroll
    // lock only lets its own content scroll and the popover is portaled outside it
    <Popover
      modal
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) setQuery("");
      }}
    >
      <PopoverTrigger asChild>
        <Button
          id={id}
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          aria-invalid={ariaInvalid}
          disabled={disabled}
          className={cn("w-full justify-between font-normal", !selected && "text-muted-foreground", className)}
        >
          {selected ? (
            <span className="flex min-w-0" title={selected.path}>
              <PathLabel option={selected} />
              {selected.archived && <span className="shrink-0 text-muted-foreground">&nbsp;(archived)</span>}
            </span>
          ) : (
            <span className="truncate">{value === null && noneLabel ? noneLabel : placeholder}</span>
          )}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] min-w-[260px] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput placeholder={searchPlaceholder} value={query} onValueChange={setQuery} />
          <CommandList>
            <CommandEmpty>{emptyMessage}</CommandEmpty>
            <CommandGroup>
              {noneLabel && !searching && (
                <CommandItem value={NONE_VALUE} onSelect={() => select(null)} className="cursor-pointer">
                  <Check className={cn("mr-2 h-4 w-4", value ? "opacity-0" : "opacity-100")} />
                  <span className="text-muted-foreground">{noneLabel}</span>
                </CommandItem>
              )}
              {visible.map((option) => (
                <CommandItem
                  key={option.id}
                  value={option.id}
                  disabled={option.disabled}
                  onSelect={() => select(option.id)}
                  className="cursor-pointer"
                >
                  <Check className={cn("mr-2 h-4 w-4 shrink-0", option.id === value ? "opacity-100" : "opacity-0")} />
                  <span
                    className="flex min-w-0"
                    title={option.path}
                    // Indent by depth while browsing; a search result shows its whole path instead
                    style={{ paddingLeft: searching ? 0 : `${(option.depth - 1) * 16}px` }}
                  >
                    {searching ? <PathLabel option={option} /> : <span className="truncate">{option.name}</span>}
                    {option.archived && <span className="shrink-0 text-muted-foreground">&nbsp;(archived)</span>}
                    {option.hint && <span className="shrink-0 text-xs text-muted-foreground">&nbsp;· {option.hint}</span>}
                  </span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
